import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NgxPermissionsModule } from 'ngx-permissions';
import {
  State,
  getCurrentUser,
  getIQObservations,
  getIQObservationsSuccess,
} from 'src/app/store';

import { toSignal } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject, combineLatest, of } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import {
  DeviceLocationsElement,
  DeviceLocationsSite,
  SynapseService,
} from 'src/app/core/services/synapse';
import { Permission } from 'src/app/shared/constants/permissions';
import { SharedModule } from 'src/app/shared/shared.module';
import { ToggleComponent } from '../../../shared/components/toggle/toggle.component';
import { LeafletMapComponent } from '../../components/leaflet-map/leaflet-map.component';
import { WidgetBillingComponent } from '../../components/widgets/widget-billing/widget-billing.component';
import { WidgetObservationsComponent } from '../../components/widgets/widget-observations/widget-observations.component';
import { WidgetOrdersComponent } from '../../components/widgets/widget-orders/widget-orders.component';
import { WidgetServicesComponent } from '../../components/widgets/widget-services/widget-services.component';
import { WidgetSummaryComponent } from '../../components/widgets/widget-summary/widget-summary.component';
import { WidgetTicketsComponent } from '../../components/widgets/widget-tickets/widget-tickets.component';
import { SERVICE_STATUS_BY_SEVERITY } from '../../lib/constants';
import { getHighestStatus } from '../../lib/functions';
import {
  Category,
  MapMarker,
  MarkerElement,
  ServiceStatus,
} from '../../lib/types';

@Component({
  selector: 'app-overall-network-status-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatProgressBarModule,
    NgxPermissionsModule,
    SharedModule,
    ToggleComponent,
    LeafletMapComponent,
    WidgetSummaryComponent,
    WidgetBillingComponent,
    WidgetTicketsComponent,
    WidgetOrdersComponent,
    WidgetServicesComponent,
    WidgetObservationsComponent,
  ],
  templateUrl: './overall-network-status-page.component.html',
  styleUrls: ['./overall-network-status-page.component.scss'],
})
export class OverallNetworkStatusPageComponent implements OnInit, OnDestroy {
  private readonly synapseService = inject(SynapseService);

  private readonly store = inject(Store<State>);

  private readonly actions = inject(Actions);

  private readonly destroy$ = new Subject<void>();

  private readonly accountName$ = this.store.select(getCurrentUser).pipe(
    map((u) => u?.Account?.Name),
    catchError((err) => {
      // eslint-disable-next-line no-console
      console.error('Error fetching user data:', err);
      return of(null);
    }),
  );

  readonly Permission = Permission;

  readonly showMockData$ = new BehaviorSubject<boolean>(false);

  readonly iqObservationsLoadings = signal<boolean>(false);

  readonly categories = signal<Category[]>([]);

  readonly markers = signal<MapMarker[]>([]);

  readonly loading = signal<boolean>(false);

  readonly observations = toSignal(
    this.actions.pipe(
      ofType(getIQObservationsSuccess),
      map((action) => {
        this.iqObservationsLoadings.set(false);
        return action.observations;
      }),
    ),
  );

  ngOnInit(): void {
    combineLatest([this.showMockData$, this.accountName$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([showMockData, accountName]) => {
        if (!accountName) return;
        this.loadIQData({
          showMockData,
          accountName,
        });
      });

    this.showMockData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((showMockData) => {
        this.iqObservationsLoadings.set(true);
        this.store.dispatch(getIQObservations({ showMockData }));
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetState(): void {
    this.loading.set(true);
    this.categories.set([]);
    this.markers.set([]);
  }

  private loadIQData(args: {
    showMockData: boolean;
    accountName: string;
  }): void {
    this.resetState();
    const params = {
      showMockData: args.showMockData,
      accountName: args.accountName,
      page: 1,
    };

    this.synapseService
      .getDeviceLocations(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.categories.set(this.getCategories(data));
          this.markers.set(this.getMarkers(data));
          this.loading.set(false);
        },
        error: (err) => {
          // eslint-disable-next-line no-console
          console.error('Error fetching device locations:', err);
          this.loading.set(false);
        },
      });
  }

  private getMarkers(args: {
    elements: DeviceLocationsElement[];
    sites: DeviceLocationsSite[];
  }): MapMarker[] {
    if (!args?.elements || !Array.isArray(args.elements)) {
      return [];
    }
    const elementsByServiceLookup = Object.fromEntries<MarkerElement[]>(
      args.elements.map((el) => [
        el.Name,
        el.Elements__r?.records?.map((element) => ({
          name: element.Name,
          recordType: {
            name: element.RecordType?.Name,
          },
          vendor: {
            circuitId: element.Vendor_Circuit_Id__c,
            name: element.Vendor__r?.Name,
          },
        })) ?? [],
      ]),
    );

    const markersLookup: Record<string, MapMarker> = {};
    args.sites.forEach((site) => {
      const key = `${site.latitude}:${site.longitude}`;

      const mapMarker: MapMarker = markersLookup[key] ?? {
        getStatus: () => getHighestStatus(mapMarker.services),
        longitude: site.longitude,
        latitude: site.latitude,
        services: [],
      };

      mapMarker.services.push({
        elements: elementsByServiceLookup[site.name] ?? [],
        status: site.status as ServiceStatus,
        recordType: site.record_type,
        source: site.source,
        name: site.name,
      });

      markersLookup[key] = mapMarker;
    });

    return Object.values(markersLookup);
  }

  private getCategories(args: {
    sites: DeviceLocationsSite[] | undefined;
  }): Category[] {
    if (!args?.sites || !Array.isArray(args.sites)) {
      return [];
    }
    const statusCountMap = args.sites.reduce(
      (acc, site) => {
        acc[site.status] = (acc[site.status] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    return [...SERVICE_STATUS_BY_SEVERITY].reverse().map((status) => ({
      label: status,
      count: statusCountMap[status] || 0,
    }));
  }
}
